import { useEffect, useState } from 'react'

import { Control, Controller, ControllerRenderProps } from 'react-hook-form'

import { Box, Collapse, Flex, Text } from '@chakra-ui/react'

import { radioButtonBorderStyles } from '@/styles/utils'
import { OptionI, Options } from '@/utils/forms/optionBuilders'

import { DatePicker } from '../DatePicker'
import { RadioButton } from '../RadioButton'
import {
  CUSTOM_INDEX,
  DateValueI,
  getCustomDates,
  getDefaultIndex,
  getEndOfDay,
  getStartOfDay,
} from './utils'

interface DateSelectIProps {
  options: Options<number | DateValueI>
  defaultValue: OptionI<number | DateValueI>
  control: Control
  id: string
}

export const DateSelect = ({
  options,
  control,
  id,
  defaultValue,
}: DateSelectIProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(
    getDefaultIndex(options, defaultValue)
  )
  const [startDate, setStartDate] = useState(
    getCustomDates(defaultValue).startDate
  )
  const [endDate, setEndDate] = useState(getCustomDates(defaultValue).endDate)
  const shouldShouldDateSelectors = selectedIndex === CUSTOM_INDEX

  const onDateOptionSelect = (
    index: number,
    onChange: (value: OptionI<number | DateValueI>) => void
  ) => {
    // Used to reset selection if option is selected twice
    const updatedIndex = index === selectedIndex ? null : index
    setSelectedIndex(updatedIndex)
    if (index === CUSTOM_INDEX) {
      const option = options[CUSTOM_INDEX]
      onChange({
        label: option.label,
        // If the index is selected add a value, otherwise set as null
        value: updatedIndex && {
          lte: getEndOfDay(new Date(endDate)),
          gte: getStartOfDay(new Date(startDate)),
        },
      })
    } else {
      onChange(options[updatedIndex])
    }
  }

  const onStartDateSelect = (
    date: Date,
    onChange: (value: OptionI<number | DateValueI>) => void
  ) => {
    setStartDate(date)
    const option = options[CUSTOM_INDEX]

    onChange({
      label: option.label,
      value: {
        lte: getEndOfDay(new Date(endDate)),
        gte: getStartOfDay(new Date(date)),
      },
    })
  }

  const onEndDateSelect = (
    date: Date,
    onChange: (value: OptionI<number | DateValueI>) => void
  ) => {
    setEndDate(date)
    const option = options[CUSTOM_INDEX]
    onChange({
      label: option.label,
      value: {
        lte: getEndOfDay(new Date(date)),
        gte: getStartOfDay(new Date(startDate)),
      },
    })
  }

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={id}
      render={({ onChange, value }: ControllerRenderProps) => {
        useEffect(() => {
          //Used to reset RadioButton Selection UI when filters are cleared
          if (!value) {
            setSelectedIndex(null)
          }
        }, [value])
        return (
          <Box>
            <Flex direction='column' gap='8px' py='15px'>
              {options?.map((item, index) => (
                <RadioButton
                  color='black'
                  isActive={selectedIndex === index}
                  key={index}
                  label={item.label}
                  onClick={() => onDateOptionSelect(index, onChange)}
                  radioButtonStyles={radioButtonBorderStyles}
                />
              ))}

              <Collapse animateOpacity in={shouldShouldDateSelectors}>
                <Flex direction='column' gap='5px' m='10px'>
                  <Text color='black' fontSize='14px' fontWeight='500'>
                    Start Date
                  </Text>
                  <DatePicker
                    borderColor='#D9D9D9'
                    date={startDate}
                    fontWeight={500}
                    maxDate={endDate}
                    minDate={null}
                    name='startDate'
                    onChange={(date) => onStartDateSelect(date, onChange)}
                  />

                  <Text color='black' fontSize='14px' fontWeight='500' mt='5px'>
                    End Date
                  </Text>
                  <DatePicker
                    borderColor='#D9D9D9'
                    date={endDate}
                    fontWeight={500}
                    maxDate={new Date()}
                    minDate={startDate}
                    name='endDate'
                    onChange={(date) => onEndDateSelect(date, onChange)}
                  />
                </Flex>
              </Collapse>
            </Flex>
          </Box>
        )
      }}
    />
  )
}
